// Lifelines row + the 4 lifeline modals (Audience Poll bar chart, Phone-a-Friend chat
// bubble, Expert Advice card). 50:50 has no modal — it just modifies AnswerPanels.
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LIFELINE_META, OPTION_LETTERS } from './kbcEngine';
import { Phone, Users, GraduationCap, ScissorsLineDashed, X } from 'lucide-react';

const LIFELINE_ICON = {
    fifty_fifty: ScissorsLineDashed,
    audience_poll: Users,
    phone_a_friend: Phone,
    expert_advice: GraduationCap,
};

export function LifelinesRow({ available, onUse, disabled }) {
    const all = ['fifty_fifty', 'audience_poll', 'phone_a_friend', 'expert_advice'];
    return (
        <div className="flex items-center justify-center gap-2 sm:gap-3" data-testid="kbc-lifelines">
            {all.map((id) => {
                const Icon = LIFELINE_ICON[id];
                const isAvailable = available.includes(id);
                return (
                    <motion.button
                        key={id}
                        type="button"
                        disabled={!isAvailable || disabled}
                        onClick={() => onUse(id)}
                        whileHover={isAvailable && !disabled ? { scale: 1.06, y: -2 } : {}}
                        whileTap={isAvailable && !disabled ? { scale: 0.94 } : {}}
                        title={`${LIFELINE_META[id].name} — ${LIFELINE_META[id].detail}${!isAvailable ? ' (used)' : ''}`}
                        data-testid={`kbc-lifeline-${id}`}
                        className={`relative inline-flex flex-col items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-full transition-all ${
                            isAvailable
                                ? 'bg-gradient-to-br from-amber-300 via-amber-400 to-amber-600 text-slate-900 shadow-[0_4px_16px_rgba(245,158,11,0.55),inset_0_2px_0_rgba(255,255,255,0.55)] ring-2 ring-amber-200'
                                : 'bg-slate-700/60 text-slate-500 opacity-50 ring-2 ring-slate-600'
                        }`}
                    >
                        <Icon className="w-5 h-5 sm:w-6 sm:h-6" strokeWidth={2.4} />
                        <span className="text-[8px] sm:text-[9px] font-display font-extrabold uppercase tracking-wide mt-0.5">
                            {LIFELINE_META[id].short}
                        </span>
                        {!isAvailable && (
                            <span className="absolute inset-0 grid place-items-center pointer-events-none">
                                <span className="w-[140%] h-0.5 bg-rose-500 rotate-[-25deg] origin-center" />
                            </span>
                        )}
                    </motion.button>
                );
            })}
        </div>
    );
}

// ---- Modal shell ----
function ModalShell({ title, onClose, children, testId, accent = 'amber' }) {
    const accentMap = {
        amber:   'from-amber-400 to-amber-600',
        violet:  'from-violet-500 to-fuchsia-700',
        emerald: 'from-emerald-400 to-emerald-600',
        sky:     'from-sky-400 to-blue-700',
    };
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 grid place-items-center px-4 backdrop-blur-md"
            style={{ background: 'radial-gradient(ellipse at center, rgba(7,12,38,0.85) 0%, rgba(0,0,0,0.95) 100%)' }}
            data-testid={testId}
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.85, y: 20, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.92, y: 12, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 240, damping: 22 }}
                onClick={(e) => e.stopPropagation()}
                className="relative max-w-md w-full bg-gradient-to-b from-slate-900 via-slate-950 to-black rounded-3xl shadow-[0_30px_90px_rgba(0,0,0,0.7)] ring-2 ring-amber-400/40 overflow-hidden"
            >
                <div className={`relative h-16 bg-gradient-to-r ${accentMap[accent]} flex items-center px-5`}>
                    <h3 className="font-display font-extrabold text-base sm:text-lg text-slate-900 tracking-wide uppercase">{title}</h3>
                    <button onClick={onClose}
                        className="ml-auto inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-900/30 hover:bg-slate-900/55 text-white"
                        data-testid="kbc-lifeline-close-btn">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="p-5 sm:p-6">{children}</div>
            </motion.div>
        </motion.div>
    );
}

// ---- Audience Poll modal ----
export function AudiencePollModal({ poll, options, onClose }) {
    const maxPct = Math.max(...poll, 1);
    return (
        <ModalShell title="Audience Poll" onClose={onClose} accent="violet" testId="kbc-modal-audience">
            <div className="text-xs text-amber-300 font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
                <Users className="w-3.5 h-3.5" /> 100 audience members voted
            </div>
            <div className="space-y-3">
                {options.map((opt, i) => {
                    const pct = poll[i] || 0;
                    const w = `${(pct / maxPct) * 100}%`;
                    return (
                        <div key={i} className="space-y-1.5" data-testid={`kbc-poll-row-${OPTION_LETTERS[i]}`}>
                            <div className="flex items-center justify-between text-[11px] sm:text-xs">
                                <span className="font-display font-extrabold text-white">{OPTION_LETTERS[i]}. <span className="font-bold opacity-80 line-clamp-1">{opt}</span></span>
                                <span className="font-display font-extrabold text-amber-300">{pct}%</span>
                            </div>
                            <div className="relative h-5 bg-slate-800/80 rounded-full overflow-hidden ring-1 ring-slate-700">
                                <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: w }}
                                    transition={{ duration: 0.9, delay: i * 0.15, ease: 'easeOut' }}
                                    className="absolute inset-y-0 left-0 rounded-full"
                                    style={{
                                        background: 'linear-gradient(90deg, #f59e0b 0%, #fde68a 100%)',
                                        boxShadow: 'inset 0 -1px 2px rgba(0,0,0,0.35), 0 0 14px rgba(251,191,36,0.5)',
                                    }}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
            <button onClick={onClose}
                className="mt-5 w-full py-2.5 rounded-xl bg-gradient-to-r from-amber-400 to-amber-600 text-slate-900 font-display font-extrabold tracking-wide shadow-lg hover:shadow-xl active:scale-95 transition"
                data-testid="kbc-poll-continue-btn">
                Continue
            </button>
        </ModalShell>
    );
}

// ---- Phone a Friend modal ----
export function PhoneAFriendModal({ hint, onClose }) {
    return (
        <ModalShell title="Phone a Friend" onClose={onClose} accent="emerald" testId="kbc-modal-friend">
            <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-14 h-14 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-700 grid place-items-center text-white font-display font-extrabold text-xl shadow-lg">
                    R
                </div>
                <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold">On the line</div>
                    <div className="font-display font-bold text-white text-sm">Rajeev · Sr. Electrician</div>
                </div>
            </div>
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="relative mt-4 rounded-2xl bg-emerald-900/30 border border-emerald-400/30 px-4 py-3"
                data-testid="kbc-friend-bubble"
            >
                <div className="absolute -top-2 left-6 w-3 h-3 rotate-45 bg-emerald-900/30 border-t border-l border-emerald-400/30" />
                <Typewriter text={hint} className="text-sm text-emerald-50 italic leading-relaxed" />
            </motion.div>
            <div className="mt-3 text-[10px] text-slate-400 italic">Friend's hint is usually right but not guaranteed. Lock kiya jaye?</div>
            <button onClick={onClose}
                className="mt-4 w-full py-2.5 rounded-xl bg-gradient-to-r from-emerald-400 to-emerald-600 text-slate-900 font-display font-extrabold tracking-wide shadow-lg hover:shadow-xl active:scale-95 transition"
                data-testid="kbc-friend-continue-btn">
                Thanks Rajeev — Continue
            </button>
        </ModalShell>
    );
}

// ---- Expert Advice modal ----
export function ExpertAdviceModal({ insight, onClose }) {
    return (
        <ModalShell title="Expert Advice" onClose={onClose} accent="sky" testId="kbc-modal-expert">
            <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-14 h-14 rounded-full bg-gradient-to-br from-sky-400 to-blue-800 grid place-items-center text-white font-display font-extrabold text-xl shadow-lg">
                    <GraduationCap className="w-7 h-7" />
                </div>
                <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-sky-300 font-bold">Senior Engineer</div>
                    <div className="font-display font-bold text-white text-sm">Dr. Ramachandran · Propulse Tech Cell</div>
                </div>
            </div>
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mt-4 rounded-2xl bg-sky-900/30 border border-sky-400/30 px-4 py-3"
                data-testid="kbc-expert-bubble"
            >
                <Typewriter text={insight} className="text-sm text-sky-50 leading-relaxed" />
            </motion.div>
            <button onClick={onClose}
                className="mt-4 w-full py-2.5 rounded-xl bg-gradient-to-r from-sky-400 to-blue-600 text-white font-display font-extrabold tracking-wide shadow-lg hover:shadow-xl active:scale-95 transition"
                data-testid="kbc-expert-continue-btn">
                Continue
            </button>
        </ModalShell>
    );
}

// ---- Walk away confirmation modal ----
export function WalkAwayConfirmModal({ amount, onConfirm, onCancel }) {
    return (
        <ModalShell title="Walk Away?" onClose={onCancel} accent="amber" testId="kbc-modal-walkaway">
            <p className="text-sm text-slate-200 leading-relaxed">
                You're about to leave the hot seat and lock in your current winnings:
            </p>
            <div className="mt-3 text-center text-3xl sm:text-4xl font-display font-extrabold text-amber-300 tracking-wide"
                style={{ textShadow: '0 0 18px rgba(251,191,36,0.6)' }}>
                {amount.toLocaleString()} pts
            </div>
            <p className="mt-3 text-xs text-slate-400 italic text-center">
                You won't see the current question's answer. Confirm to leave with what you've already locked.
            </p>
            <div className="grid grid-cols-2 gap-3 mt-5">
                <button onClick={onCancel}
                    className="py-2.5 rounded-xl bg-slate-800 text-slate-200 font-display font-extrabold hover:bg-slate-700 active:scale-95 transition"
                    data-testid="kbc-walkaway-cancel-btn">
                    Keep Playing
                </button>
                <button onClick={onConfirm}
                    className="py-2.5 rounded-xl bg-gradient-to-r from-amber-400 to-amber-600 text-slate-900 font-display font-extrabold shadow-lg hover:shadow-xl active:scale-95 transition"
                    data-testid="kbc-walkaway-confirm-btn">
                    Walk Away
                </button>
            </div>
        </ModalShell>
    );
}

// ---- Lock-it confirmation panel ----
export function LockInConfirmModal({ letter, text, multiplier, award, onConfirm, onCancel }) {
    return (
        <ModalShell title="Lock kiya jaye?" onClose={onCancel} accent="amber" testId="kbc-modal-lockin">
            <p className="text-xs text-slate-400 mb-2 uppercase tracking-widest font-bold">Your final answer is</p>
            <div className="rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 px-4 py-3 ring-2 ring-amber-200/70 shadow-lg">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full grid place-items-center bg-slate-900 text-amber-300 font-display font-extrabold">
                        {letter}:
                    </div>
                    <div className="font-display font-bold text-slate-900 text-sm">{text}</div>
                </div>
            </div>
            {multiplier ? (
                <div className="mt-3 grid grid-cols-2 gap-2">
                    <div className="rounded-xl bg-slate-800/70 ring-1 ring-slate-700 px-3 py-2 text-center">
                        <div className="text-[9px] uppercase tracking-widest text-slate-400 font-bold">Speed Multiplier</div>
                        <div className="font-display font-extrabold text-2xl"
                             style={{ color: multiplier >= 4 ? '#34d399' : multiplier === 3 ? '#fde047' : multiplier === 2 ? '#fb923c' : '#ef4444' }}>
                            {multiplier}x
                        </div>
                    </div>
                    <div className="rounded-xl bg-emerald-900/30 ring-1 ring-emerald-500/40 px-3 py-2 text-center">
                        <div className="text-[9px] uppercase tracking-widest text-emerald-300 font-bold">If Correct</div>
                        <div className="font-display font-extrabold text-2xl text-emerald-300">+{award.toLocaleString()}</div>
                    </div>
                </div>
            ) : null}
            <div className="grid grid-cols-2 gap-3 mt-5">
                <button onClick={onCancel}
                    className="py-2.5 rounded-xl bg-slate-800 text-slate-200 font-display font-extrabold hover:bg-slate-700 active:scale-95 transition"
                    data-testid="kbc-lockin-cancel-btn">
                    Change
                </button>
                <button onClick={onConfirm}
                    className="py-2.5 rounded-xl bg-gradient-to-r from-rose-500 to-rose-700 text-white font-display font-extrabold shadow-[0_0_22px_rgba(244,63,94,0.55)] hover:shadow-xl active:scale-95 transition"
                    data-testid="kbc-lockin-confirm-btn">
                    Lock It
                </button>
            </div>
        </ModalShell>
    );
}

// ---- End-of-game modal (won / walked / lost) ----
export function EndGameModal({ outcome, points, levelReached, onPlayAgain, onExit }) {
    const isWin = outcome === 'won';
    const isWalk = outcome === 'walked';
    const isLost = outcome === 'lost';
    const accent = isWin ? 'amber' : isWalk ? 'sky' : 'amber';
    const title = isWin ? '🏆 You Won the Jackpot!' : isWalk ? '👋 Walked Away' : '💔 Game Over';
    return (
        <ModalShell title={title} onClose={onExit} accent={accent} testId={`kbc-modal-end-${outcome}`}>
            <div className="text-center py-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Final Winnings</div>
                <motion.div
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: [0.5, 1.15, 1], opacity: 1 }}
                    transition={{ duration: 0.7, ease: 'easeOut' }}
                    className="mt-2 text-5xl sm:text-6xl font-display font-extrabold tracking-wide"
                    style={{
                        color: isLost && points === 0 ? '#94a3b8' : '#fde047',
                        textShadow: isWin
                            ? '0 0 40px rgba(253,224,71,0.85), 0 0 12px rgba(253,224,71,0.8)'
                            : '0 0 18px rgba(253,224,71,0.4)',
                    }}
                    data-testid="kbc-final-points"
                >
                    {points.toLocaleString()} pts
                </motion.div>
                <div className="mt-3 text-sm text-slate-300">
                    {isWin   && 'Crorepati! You answered all 16 questions correctly.'}
                    {isWalk  && `You walked away after locking Q${levelReached}.`}
                    {isLost  && `You fell at Q${levelReached + 1}. Your safe milestone kicks in.`}
                </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
                <button onClick={onExit}
                    className="py-2.5 rounded-xl bg-slate-800 text-slate-200 font-display font-extrabold hover:bg-slate-700 active:scale-95 transition"
                    data-testid="kbc-end-exit-btn">
                    Exit to Arcade
                </button>
                <button onClick={onPlayAgain}
                    className="py-2.5 rounded-xl bg-gradient-to-r from-amber-400 to-amber-600 text-slate-900 font-display font-extrabold shadow-lg hover:shadow-xl active:scale-95 transition"
                    data-testid="kbc-end-play-again-btn">
                    Play Again
                </button>
            </div>
        </ModalShell>
    );
}

// ---- Explanation modal (after every locked answer) ----
export function ExplanationModal({ correct, explanation, onNext, isLast }) {
    return (
        <ModalShell
            title={correct ? '✅ Correct!' : '❌ Wrong'}
            onClose={onNext}
            accent={correct ? 'emerald' : 'amber'}
            testId="kbc-modal-explanation"
        >
            <p className="text-sm text-slate-200 leading-relaxed">{explanation}</p>
            <button onClick={onNext}
                className={`mt-5 w-full py-2.5 rounded-xl font-display font-extrabold tracking-wide shadow-lg hover:shadow-xl active:scale-95 transition ${
                    correct
                        ? 'bg-gradient-to-r from-emerald-400 to-emerald-600 text-slate-900'
                        : 'bg-gradient-to-r from-amber-400 to-amber-600 text-slate-900'
                }`}
                data-testid="kbc-explanation-next-btn">
                {isLast ? 'See Final Score' : 'Next Question'}
            </button>
        </ModalShell>
    );
}

// ---- Topic Picker modal ----
export function TopicPickerModal({ presets, onPick, onClose }) {
    const [custom, setCustom] = React.useState('');
    return (
        <ModalShell title="Choose Your Topic" onClose={onClose} accent="amber" testId="kbc-modal-topic">
            <p className="text-xs text-slate-400 mb-3">
                We'll generate 16 fresh questions on the topic you pick — easy at the start, expert-level at the top.
            </p>
            <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                {presets.map((p) => (
                    <button key={p}
                        onClick={() => onPick(p)}
                        className="w-full text-left px-3 py-2.5 rounded-xl bg-slate-800/70 hover:bg-amber-500/20 border border-slate-700 hover:border-amber-400/60 text-sm text-slate-100 font-display font-bold transition"
                        data-testid={`kbc-topic-${p.slice(0, 16).replace(/\s+/g, '-').toLowerCase()}`}>
                        {p}
                    </button>
                ))}
            </div>
            <div className="mt-4 pt-4 border-t border-slate-700">
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-1.5">Or type your own</div>
                <div className="flex gap-2">
                    <input value={custom} onChange={(e) => setCustom(e.target.value)}
                        placeholder="e.g. control cables troubleshooting"
                        className="flex-1 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-400"
                        data-testid="kbc-topic-input" />
                    <button onClick={() => custom.trim() && onPick(custom.trim())}
                        disabled={!custom.trim()}
                        className="px-4 py-2 rounded-lg bg-gradient-to-r from-amber-400 to-amber-600 text-slate-900 font-display font-extrabold disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 transition"
                        data-testid="kbc-topic-go-btn">
                        Go
                    </button>
                </div>
            </div>
            <div className="mt-4 text-center text-[10px] text-slate-500">
                Use curated bank instead → click <kbd className="px-1 py-0.5 rounded bg-slate-800 text-slate-300 text-[10px]">Close</kbd>
            </div>
        </ModalShell>
    );
}

// ---- Tiny typewriter helper ----
function Typewriter({ text, className }) {
    const [shown, setShown] = React.useState('');
    React.useEffect(() => {
        setShown('');
        let i = 0;
        const id = setInterval(() => {
            i += 2;
            setShown(text.slice(0, i));
            if (i >= text.length) clearInterval(id);
        }, 18);
        return () => clearInterval(id);
    }, [text]);
    return <p className={className}>{shown}<span className="inline-block w-1 h-3 bg-current opacity-70 ml-0.5 animate-pulse" /></p>;
}

export const KBCModalRoot = AnimatePresence;
